import { useState } from 'react';
import { Rocket, CheckCircle, AlertCircle } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { LoadingSpinner } from '@/components/LoadingSpinner';
import { useAuth } from '@/auth/useAuth';
import { seedSystem } from '@/api/users-api';

export function SetupPage() {
  const { user, refreshAuth } = useAuth();
  const [isSeeding, setIsSeeding] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);

  async function handleSeed() {
    setIsSeeding(true);
    setError(null);

    try {
      await seedSystem();
      setSuccess(true);
      // Refresh auth to get the new role/permissions/menus
      await refreshAuth();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to initialize system');
    } finally {
      setIsSeeding(false);
    }
  }

  if (success) {
    return (
      <div className="flex items-center justify-center min-h-screen bg-background">
        <Card className="w-full max-w-md">
          <CardHeader className="text-center">
            <CheckCircle className="w-16 h-16 mx-auto text-green-500 mb-4" />
            <CardTitle>Setup Complete!</CardTitle>
            <CardDescription>
              The system has been initialized and you are now a Super Administrator.
            </CardDescription>
          </CardHeader>
          <CardContent className="text-center">
            <Button onClick={() => window.location.reload()}>
              Go to Dashboard
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="flex items-center justify-center min-h-screen bg-background">
      <Card className="w-full max-w-md">
        <CardHeader className="text-center">
          <Rocket className="w-16 h-16 mx-auto text-primary mb-4" />
          <CardTitle>Welcome to Integration Studio</CardTitle>
          <CardDescription>
            This appears to be a fresh installation. Click below to initialize the system
            with default roles, menus, and set yourself as the Super Administrator.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="text-sm text-muted-foreground bg-muted p-3 rounded-md">
            <p><strong>Email:</strong> {user?.signInDetails?.loginId ?? 'Unknown'}</p>
            <p className="mt-1">You will be assigned the <strong>Super Administrator</strong> role.</p>
          </div>

          {error && (
            <div className="flex items-center gap-2 text-destructive text-sm bg-destructive/10 p-3 rounded-md">
              <AlertCircle className="w-4 h-4" />
              {error}
            </div>
          )}

          <Button
            className="w-full"
            size="lg"
            onClick={handleSeed}
            disabled={isSeeding}
          >
            {isSeeding ? (
              <>
                <LoadingSpinner size="sm" className="mr-2" />
                Initializing...
              </>
            ) : (
              'Initialize System'
            )}
          </Button>
        </CardContent>
      </Card>
    </div>
  );
}
